import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import isEmpty from 'lodash/isEmpty';
import { switchMap, filter, first } from 'rxjs/operators';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getRoleMatrix, getContainerProjectId } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Container } from '../../shared/models/container.model';
import { Note } from '../../shared/models/note.model';
import { ConfirmDialog, DialogService } from '../../dialogs';

@Component({
  selector: 'container-notes',
  styleUrls: ['./container-notes.component.scss'],
  templateUrl: './container-notes.component.html',
})
export class ContainerNotes implements OnInit, OnChanges {
  @Input() container: Container
  @Input() notes: Note[] = []

  @Output() afterSave = new EventEmitter<Note[]>()

  public newNote = ''
  public projectId: string;
  public canManageNotes = false;

  constructor(
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
  ) { }

  ngOnInit(): void {
    if ((this.notes || []).length === 0 || !(this.notes || []).every(note => Boolean(note.avatar))) {
      this.refreshData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.container && this.container?._id
      && (changes.container.firstChange || this.container._id !== changes.container.previousValue?._id)) {
      this.updatePermissions();
    }
  }

  saveNote(): void {
    this.coreApi.containers.notes.post(this.container._id, { text: this.newNote })
      .subscribe({
        next: () => {
          this.newNote = '';
          this.notifications.success('container.note.add.success');
          this.refreshData();
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('container.note.add.error');
        },
      });
  }

  deleteNote(noteId): void {
    this.dialog.open(ConfirmDialog, { data: { content: 'Are you sure you want to delete this note?' } })
      .afterClosed().pipe(
        filter(proceed => Boolean(proceed)),
        switchMap(() => {
          return this.coreApi.containers.notes.delete(this.container._id, noteId);
        }),
      ).subscribe({
        next: () => {
          this.notifications.success('container.note.delete.success');
          this.refreshData();
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('container.note.delete.error');
        },
      });
  }

  private refreshData(): void {
    if (this.container?._id) {
      this.coreApi.containers.get<Container>(this.container._id, { join_avatars: true })
      .subscribe(container => {
        this.container = container;
        this.notes = this.container.notes;
        this.afterSave.emit(this.notes);
      });
    }
  }

  private updatePermissions(): void {
    if (isEmpty(this.container)){
      return;
    }
    const projectId = getContainerProjectId(this.container);
    if (projectId) {
      this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.canManageNotes = projectPermissions.includes(PermissionAction.NotesManage);
      });
    } else if (this.container && this.container.permissions) {
      // Fall back to roleMatrix if the container doesn't have roles
      this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.canManageNotes = roleMatrix[ContainerAccess.ReadWrite];
      });
    }
  }
}
